/**
 * Manda in fatturazione un lavoro a corpo: tutto o in parte, nel mese che
 * decidi, anche in più rate mensili.
 *
 * Il lavoro a corpo non ha avanzamenti da rendicontare — il prezzo è quello
 * dell'offerta — e il SAL diventa un passaggio a vuoto. Qui si dice solo
 * quanto fatturare e quando: nascono righe "da emettere" col mese di
 * competenza, e il residuo della commessa scala da solo.
 */
import { useEffect, useState } from 'react';
import { fatturazionIApi } from '../api';
import Modal from './Modal';
import CampoImporto from './CampoImporto';
import SceltaMese from './SceltaMese';

const fmt = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
const label = 'block text-xs font-medium text-gray-500 mb-0.5';
const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];
const meseEsteso = (iso) => {
  const [a, m] = String(iso || '').split('-').map(Number);
  return m ? `${MESI[m - 1]} ${a}` : '—';
};
const meseDopo = (iso, n) => {
  const [a, m] = String(iso || '').split('-').map(Number);
  if (!m) return iso;
  const d = new Date(a, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const meseCorrente = () => new Date().toISOString().slice(0, 7);

export default function MandaInFatturazione({ commessaId, titolo, onCambiato, compatto }) {
  const [aperto, setAperto] = useState(false);
  const [sit, setSit] = useState(null);
  const [lavorando, setLavorando] = useState(false);
  const [f, setF] = useState(null);

  useEffect(() => {
    if (!aperto) return;
    fatturazionIApi.daMandare(commessaId)
      .then(s => {
        setSit(s);
        setF({ modo: 'tutto', valore: '', competenza: meseCorrente(), rate: 1,
               descrizione: titolo || s.titolo || '' });
      })
      .catch(e => { alert(e); setAperto(false); });
  }, [aperto, commessaId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const n = (v) => Number(String(v ?? '').replace(',', '.')) || 0;
  const importo = !sit || !f ? 0
    : f.modo === 'tutto' ? sit.da_decidere
    : f.modo === 'percentuale' ? Math.round(sit.budget * n(f.valore)) / 100
    : n(f.valore);
  const rate = Math.min(Math.max(parseInt(f?.rate, 10) || 1, 1), 24);
  const oltre = sit && importo > sit.da_decidere + 0.005;

  const manda = async (confermaOltre) => {
    setLavorando(true);
    try {
      await fatturazionIApi.pianifica({
        commessa_id: commessaId, modo: f.modo, valore: f.valore,
        competenza: f.competenza, rate, descrizione: f.descrizione,
        conferma_oltre_budget: !!confermaOltre,
      });
      setAperto(false);
      onCambiato?.();
    } catch (e) {
      // Oltre il residuo si può andare, ma lo deve dire l'utente.
      const messaggio = String(e?.error || e || '');
      if (e?.conferma_richiesta === 'conferma_oltre_budget' || /Restano .* da fatturare/.test(messaggio)) {
        if (confirm(`${messaggio}\n\nVuoi mandarla comunque in fatturazione?`)) return manda(true);
      } else alert(e);
    } finally { setLavorando(false); }
  };

  return (
    <>
      <button className={`whitespace-nowrap ${compatto ? 'btn-secondary btn-sm' : 'btn-primary'}`}
              title="Manda in fatturazione il lavoro a corpo, tutto o in parte"
              onClick={() => setAperto(true)}>
        💶 Manda in fatturazione
      </button>

      {aperto && (
        <Modal title="Manda in fatturazione" onClose={() => setAperto(false)} size="md">
          {/* text-left: la finestra si apre anche da una cella di tabella
              allineata a destra, e senza questo erediterebbe l'allineamento. */}
          <div className="text-left">
          {!sit || !f ? <div className="text-gray-400 text-sm py-6">Caricamento…</div> : (
            <>
              <div className="grid grid-cols-4 gap-2 text-sm mb-4">
                {[['Valore commessa', sit.budget], ['Già fatturato', sit.fatturato],
                  ['In coda', sit.in_coda], ['Ancora da decidere', sit.da_decidere]].map(([k, v], i) => (
                  <div key={k} className={`border rounded-lg px-2.5 py-2 ${i === 3 ? 'bg-brand-50 border-brand-200' : 'bg-white'}`}>
                    <div className="text-[11px] text-gray-500">{k}</div>
                    <div className={`font-semibold ${i === 3 ? 'text-brand-800' : ''}`}>{fmt(v)}</div>
                  </div>
                ))}
              </div>

              <div className="space-y-3 text-sm">
                <div>
                  <div className={label}>Quanto</div>
                  <div className="flex flex-wrap gap-3 items-center">
                    {[['tutto', 'Tutto quello che resta'], ['percentuale', 'Una percentuale'], ['importo', 'Un importo']]
                      .map(([v, l]) => (
                      <label key={v} className="flex items-center gap-1.5">
                        <input type="radio" name="modo" className="w-4 h-4" checked={f.modo === v}
                               onChange={() => setF(p => ({ ...p, modo: v, valore: '' }))} />
                        <span>{l}</span>
                      </label>
                    ))}
                    {f.modo === 'percentuale' && (
                      <div className="flex items-center gap-1 w-28">
                        <input className="input py-1 text-sm text-right" inputMode="decimal" autoFocus
                               value={f.valore} onChange={e => setF(p => ({ ...p, valore: e.target.value }))} />
                        <span className="text-gray-500">%</span>
                      </div>
                    )}
                    {f.modo === 'importo' && (
                      <div className="w-32">
                        <CampoImporto className="input py-1 text-sm" value={f.valore}
                                      onChange={v => setF(p => ({ ...p, valore: v }))} />
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={label}>Mese in cui fatturare</label>
                    <SceltaMese className="input py-1 text-sm" value={f.competenza}
                                onChange={v => setF(p => ({ ...p, competenza: v }))} />
                  </div>
                  <div>
                    <label className={label}>In quante fatture mensili</label>
                    <input type="number" min="1" max="24" className="input py-1 text-sm" value={f.rate}
                           onChange={e => setF(p => ({ ...p, rate: e.target.value }))} />
                  </div>
                </div>

                <div>
                  <label className={label}>Descrizione in fattura</label>
                  <input className="input py-1 text-sm" value={f.descrizione}
                         onChange={e => setF(p => ({ ...p, descrizione: e.target.value }))} />
                </div>

                {/* Cosa succederà, in una riga: è l'unica verifica che serve. */}
                <div className={`rounded px-2.5 py-2 text-xs border ${oltre
                  ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                  {importo > 0 ? (
                    <>
                      {rate > 1
                        ? <>Nascono <strong>{rate} fatture da emettere</strong> da circa {fmt(importo / rate)} ciascuna,
                            da {meseEsteso(f.competenza)} a {meseEsteso(meseDopo(f.competenza, rate - 1))},
                            per un totale di <strong>{fmt(importo)}</strong>.</>
                        : <>Nasce <strong>una fattura da emettere</strong> di {fmt(importo)} con competenza {meseEsteso(f.competenza)}.</>}
                      {oltre && <> Attenzione: sono {fmt(importo - sit.da_decidere)} oltre quello che resta da fatturare.</>}
                    </>
                  ) : 'Indica quanto mandare in fatturazione.'}
                </div>
              </div>
            </>
          )}
          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setAperto(false)}>Annulla</button>
            <button className="btn-primary" disabled={lavorando || !(importo > 0)} onClick={() => manda(false)}>
              {lavorando ? '…' : 'Manda in fatturazione'}
            </button>
          </div>
          </div>
        </Modal>
      )}
    </>
  );
}
